import { Button, ButtonVariant, ButtonColorVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useId, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { BaseConfirmDialogProps } from "./_types";

export function BaseConfirmDialog({
  open,
  title,
  description,
  customComponent,
  labelCancel,
  labelConfirm,
  onCancel,
  onConfirm,
  requireConfirmation = false,
  confirmationPlaceholder = "",
  confirmationLabel = "",
  expectedConfirmationText = "",
  cancelVariant = ButtonVariant.Outlined,
  confirmVariant = ButtonVariant.Filled,
  confirmColorVariant = ButtonColorVariant.Primary,
}: BaseConfirmDialogProps): ReactElement {
  const { t } = useTranslation("common");
  const [confirmationText, setConfirmationText] = useState("");
  const confirmationId = useId();

  const isConfirmationValid = !requireConfirmation || confirmationText === expectedConfirmationText;

  const handleConfirm = (): void => {
    if (isConfirmationValid) {
      onConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent onClose={onCancel}>
        <DialogHeader>
          <DialogTitle>{title ?? t("AreYouSure")}</DialogTitle>
          {description && <DialogDescription className="mt-2">{description}</DialogDescription>}
        </DialogHeader>

        {customComponent && <div className="py-2">{customComponent}</div>}

        {requireConfirmation && (
          <div className="flex flex-col space-y-2">
            <Label htmlFor={confirmationId} allowSelection>
              {confirmationLabel}
            </Label>
            <Input
              id={confirmationId}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={confirmationPlaceholder}
              error={!isConfirmationValid && confirmationText.length > 0}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={onCancel}
            onKeyDown={(e) => e.key === "Enter" && onCancel()}
            variant={cancelVariant}
            colorVariant={ButtonColorVariant.Secondary}
          >
            {labelCancel ?? t("No")}
          </Button>
          <Button
            onClick={handleConfirm}
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            variant={confirmVariant}
            colorVariant={confirmColorVariant}
            disabled={!isConfirmationValid}
          >
            {labelConfirm ?? t("Yes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
